import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { EVA_RESEARCH_NAME } from "constants/app.constants";
import { MCDANIEL_SEARCH_FOLDER_NAME } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import { getPropertiesWithForecast, injectGroupProperty } from "utils/data-table";
import { toLocalYYYYMMDD } from "utils/dateFormatters";
import { ExportFormat, exportFileName } from "utils/exportUtils";

import { useUser, useUserEvents } from "hooks";

import { toGroupModeRequestDto } from "api/data";

import { useGroupModeState } from "components/filter/production/hooks/useGroupModeController";
import { useGroupByState } from "components/groupBy/group-by.context";
import { ExportIconButton } from "components/shared/ExportIconButton";

import useFeatureFlags from "../../hooks/useFeatureFlags";
import { EntityKind } from "../../models/entityKind";
import { useTable } from "./context";
import { useExportMutation } from "./hooks";

export interface ISaveButton {
  selectedProperties: string[];
}

const ExportButton = ({
  selectedProperties,
  dataSources,
  resourcePlays,
  rowCount,
  fieldGroups
}) => {
  const [{ entityKind, dataTableView }] = useTable();
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well ? state.filter.filterId : state.facilityFilter.filterId
  );
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const { hasTrialAccount, isReadonly } = useUser();
  const { hasFeature } = useFeatureFlags();
  const { sendExportDataTableEvent } = useUserEvents();
  const hasMidstreamColumns =
    Array.isArray(selectedProperties) &&
    selectedProperties.some((p) => p.startsWith("Midstream."));
  const canExportMidstream =
    (entityKind == EntityKind.Facility || hasMidstreamColumns) &&
    hasFeature("Midstream Export");
  const canExport =
    !isReadonly &&
    !hasTrialAccount &&
    (entityKind == EntityKind.Well || canExportMidstream);
  const { selectedForecastFolderName, selectedPdenSource } = useGroupByState();
  const { isGroupMode, groupBy } = useGroupModeState(entityKind ?? EntityKind.Well);
  // The internal sentinel value for the default forecast source is still
  // MCDANIEL_SEARCH_FOLDER_NAME (relied on throughout groupBy/property-qualification
  // logic) — only translate it to the current display name here, for the file itself.
  const exportForecastSourceName =
    selectedForecastFolderName === MCDANIEL_SEARCH_FOLDER_NAME
      ? EVA_RESEARCH_NAME
      : selectedForecastFolderName;

  const effectivePropertiesForExport = useMemo(() => {
    return injectGroupProperty(selectedProperties);
  }, [isGroupMode, dataTableView, groupBy?.mode?.property, selectedProperties]);

  const groupModeForExport = useMemo(() => {
    if (!isGroupMode || !groupBy?.mode) return undefined;
    if (dataTableView === "group") {
      const dto = toGroupModeRequestDto(groupBy.mode);
      return dto ? { ...dto, wellView: false } : undefined;
    }
    // Well view: always send groupMode with wellView:true — mirrors useDataQuery behaviour.
    // The backend needs group context even for non-binned/text group fields so that
    // each well row carries its group membership (e.g. Operator, ResourcePlay).
    const dto = toGroupModeRequestDto(groupBy.mode);
    return dto ? { ...dto, wellView: true } : undefined;
  }, [isGroupMode, dataTableView, groupBy?.mode]);

  const exportFile = (response, fileName) => {
    const encodedUri = URL.createObjectURL(response);
    const link = document.createElement("a");
    link.href = encodedUri;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMutation = useExportMutation(
    (response, variables) => {
      const filename = exportFileName(
        variables.format === "Excel" ? ExportFormat.Excel : ExportFormat.CSV
      );
      exportFile(response.data, filename);
    },
    (error) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as any;
      if (err && err.response?.data) {
        // eslint-disable-next-line no-console
        console.error("Export error", err.response?.data);
      }
      toast.error("Failed to export data");
    }
  );

  const runExport = (format: ExportFormat) => {
    if (hasMidstreamColumns && !canExportMidstream) {
      toast.error("Your account does not have access to export midstream columns.");
      return;
    }
    const properties = getPropertiesWithForecast(
      effectivePropertiesForExport,
      dataSources
    );
    const requestFormat = format === ExportFormat.Excel ? "Excel" : "CSV";

    // Send user event for data table export
    sendExportDataTableEvent({
      format: requestFormat,
      entityKind,
      propertiesCount: properties.length,
      wellCount: rowCount,
      resourcePlaysCount: resourcePlays.length,
      resourcePlays: resourcePlays,
      fieldGroups: fieldGroups
    });

    exportMutation.mutate({
      filterId: filterId.id,
      properties: properties,
      format: requestFormat,
      forecastSource: entityKind === EntityKind.Facility ? "" : exportForecastSourceName,
      pdenSource: selectedPdenSource,
      syncJob: syncWells?.isSync ? syncWells.schema : undefined,
      entityKind,
      normalization: {
        useNormalizeBy,
        globalNormalizeBy
      },
      groupMode: groupModeForExport,
      ...(dataSources.startDate && dataSources.endDate
        ? {
            performanceDateRange: {
              startDate: toLocalYYYYMMDD(dataSources.startDate),
              endDate: toLocalYYYYMMDD(dataSources.endDate)
            }
          }
        : {})
    });
  };

  return !canExport ? null : (
    <ExportIconButton
      tooltip="Export Data Table"
      loading={exportMutation.isLoading}
      onSelect={runExport}
    />
  );
};

export default memo(ExportButton);
